/**
 * 3D perspective camera for Complex (ℂ) tunnel mode.
 *
 * Provides a 4x4 model-view-projection matrix for transforming
 * 3D world coordinates to clip space. Camera looks down -Z.
 */

export class Camera3D {
  /** Camera position in 3D */
  positionX = 0;
  positionY = 0;
  positionZ = 0;

  /** Field of view in radians */
  fov = Math.PI / 3; // 60 degrees

  /** Near/far clipping planes */
  near = 1;
  far = 800;

  /** Aspect ratio (set on resize) */
  aspect = 16 / 9;

  /** Screen shake */
  private shakeIntensity = 0;
  private shakeOffsetX = 0;
  private shakeOffsetY = 0;

  shake(intensity: number): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
  }

  update(dt: number): void {
    if (this.shakeIntensity > 0.01) {
      this.shakeOffsetX = (Math.random() * 2 - 1) * this.shakeIntensity;
      this.shakeOffsetY = (Math.random() * 2 - 1) * this.shakeIntensity;
      this.shakeIntensity *= Math.exp(-5 * dt);
    } else {
      this.shakeIntensity = 0;
      this.shakeOffsetX = 0;
      this.shakeOffsetY = 0;
    }
  }

  resize(width: number, height: number): void {
    this.aspect = width / height;
  }

  /** Get the combined view-projection matrix (column-major 4x4) */
  getMVP(): Float32Array {
    const cx = this.positionX + this.shakeOffsetX;
    const cy = this.positionY + this.shakeOffsetY;
    const cz = this.positionZ;

    // Perspective projection (OpenGL convention: camera looks down -Z)
    const f = 1 / Math.tan(this.fov / 2);
    const nf = 1 / (this.near - this.far);

    // P * V combined (V is just translation by -camera position)
    // Since V = translate(-cx, -cy, -cz), we can fold it into P
    const tx = -cx;
    const ty = -cy;
    const tz = -cz;

    // Column-major P * V
    return new Float32Array([
      f / this.aspect, 0, 0, 0,
      0, f, 0, 0,
      tx * 0, ty * 0 , (this.far + this.near) * nf, -1,
      f / this.aspect * tx, f * ty, (this.far + this.near) * nf * tz + 2 * this.far * this.near * nf, -tz,
    ]);
  }

  /** Cleaner: compute P and V separately, then multiply */
  getViewProjectionMatrix(): Float32Array {
    const cx = this.positionX + this.shakeOffsetX;
    const cy = this.positionY + this.shakeOffsetY;
    const cz = this.positionZ;

    const f = 1 / Math.tan(this.fov / 2);
    const nf = 1 / (this.near - this.far);

    // Projection (column-major)
    const p00 = f / this.aspect;
    const p11 = f;
    const p22 = (this.far + this.near) * nf;
    const p23 = -1;
    const p32 = 2 * this.far * this.near * nf;

    // View = translate(-cx, -cy, -cz) (column-major)
    // V = [1,0,0,0, 0,1,0,0, 0,0,1,0, -cx,-cy,-cz,1]

    // P * V (manually, since V is just translation):
    // Result column 3 = P * [-cx, -cy, -cz, 1]
    return new Float32Array([
      p00, 0,   0,   0,
      0,   p11, 0,   0,
      0,   0,   p22, p23,
      p00 * (-cx), p11 * (-cy), p22 * (-cz) + p32, p23 * (-cz),
    ]);
  }
}
